import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApi, money } from "@/lib/api";
import { useMe } from "@/components/portal/MeContext";
import { ClockWidget } from "@/components/portal/ClockWidget";
import { WelcomeChecklist } from "@/components/portal/WelcomeChecklist";
import { ProjectCard } from "@/components/portal/ProjectCard";
import { PageHeader, Empty, StatusBadge } from "@/components/portal/StatusBadge";

const Stat = ({ label, value, testId }) => (
  <div className="rounded-xl border border-[#E6E2DA] bg-white p-5">
    <p className="eyebrow">{label}</p>
    <p data-testid={testId} className="font-mono-num mt-2 text-2xl font-semibold">{value}</p>
  </div>
);

const AdminDash = () => {
  const api = useApi();
  const [s, setS] = useState(null);
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    api("get", "/stats").then(setS);
    api("get", "/projects").then(setProjects);
  }, [api]);
  return (
    <>
      {s && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Stat label="Active projects" value={s.active_projects} testId="stat-active-projects" />
          <Stat label="Open estimates" value={s.open_estimates} testId="stat-open-estimates" />
          <Stat label="Unpaid" value={money(s.unpaid_total)} testId="stat-unpaid" />
          <Stat label="Collected" value={money(s.paid_total)} testId="stat-paid" />
          <Stat label="New leads" value={s.new_leads} testId="stat-new-leads" />
          <Stat label="On the clock" value={s.clocked_in} testId="stat-clocked-in" />
          <Stat label="Visit requests" value={s.visit_requests} testId="stat-visit-requests" />
          <Stat label="Subscribers" value={s.subscribers} testId="stat-subscribers" />
        </div>
      )}
      <h2 className="font-display mb-4 mt-10 text-xl font-bold">Recent projects</h2>
      {projects.length ? <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{projects.slice(0, 6).map((p) => <ProjectCard key={p.id} p={p} />)}</div> : <Empty text="No projects yet. Create one from the Projects page." />}
    </>
  );
};

const EmployeeDash = () => {
  const api = useApi();
  const [tasks, setTasks] = useState([]);
  useEffect(() => {
    api("get", "/tasks").then(setTasks);
  }, [api]);
  const open = tasks.filter((t) => t.status !== "done");
  return (
    <>
    <WelcomeChecklist />
    <div className="grid gap-6 lg:grid-cols-2">
      <ClockWidget />
      <div className="rounded-2xl border border-[#E6E2DA] bg-white p-6 sm:p-8">
        <p className="eyebrow mb-4">My open tasks</p>
        {open.length ? open.map((t) => (
          <div key={t.id} className="flex items-center justify-between border-b border-[#E6E2DA] py-3 last:border-0">
            <div><p className="font-semibold">{t.title}</p><p className="text-xs text-[#5C5850]">{t.project_name || "General"}</p></div>
            <StatusBadge status={t.status} />
          </div>
        )) : <p className="text-sm text-[#5C5850]">No open tasks.</p>}
        <Link to="/portal/tasks" className="mt-4 inline-block text-sm font-semibold text-[#A67C00]" data-testid="dash-all-tasks-link">All tasks →</Link>
      </div>
    </div>
    </>
  );
};

const CustomerDash = () => {
  const api = useApi();
  const me = useMe();
  const [projects, setProjects] = useState(null);
  useEffect(() => {
    api("get", "/projects").then(setProjects);
  }, [api]);
  if (!projects) return null;
  return projects.length ? (
    <div className="grid gap-4 md:grid-cols-2">{projects.map((p) => <ProjectCard key={p.id} p={p} />)}</div>
  ) : (
    <Empty testId="customer-no-projects" text={`No projects linked to ${me.email || "your account"} yet. Once Firm Foundations sets up your job, it will appear here with live updates. Questions? Call 760-590-6672.`} />
  );
};

export default function Dashboard() {
  const me = useMe();
  const titles = { admin: "Business overview", employee: "Your day", customer: "Your projects" };
  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow={`Welcome, ${me.name || "there"}`} title={titles[me.role]} />
      {me.role === "admin" && <AdminDash />}
      {me.role === "employee" && <EmployeeDash />}
      {me.role === "customer" && <CustomerDash />}
    </div>
  );
}
